import React, { createContext, useContext, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  setApplicationNotificationsBridgeContext,
  getApplicationNotificationsBridgeContext,
} from './bridge'
import { DebuggingNotifier } from './debuggingNotifier'
import { PageBannerNotifier } from './pageBannerNotifier'
import { OverlayBannerNotifier } from './overlayBannerNotifier'
import { ToastNotifier } from './toastNotifier'
import { getNullApplicationNotifications } from './getNullApplicationNotifications'
import { NOTIFICATION_ROLES, DEFAULT_NOTIFICATION_OPTIONS } from './constants'
import { useMountEffect } from '../../hooks'

type Notifications = {
  debugger: DebuggingNotifier | null
  toast: ToastNotifier | null
  overlayBanner: OverlayBannerNotifier | null
  pageBanner: PageBannerNotifier | null
  roles: typeof NOTIFICATION_ROLES
}

const ApplicationNotificationsContext = createContext<Notifications>({
  debugger: null,
  toast: null,
  overlayBanner: null,
  pageBanner: null,
  roles: NOTIFICATION_ROLES,
})

const ApplicationNotificationsConsumer =
  ApplicationNotificationsContext.Consumer

interface ApplicationNotificationsProviderProps {
  children: React.ReactNode
  pageBannerContainerRef?: React.RefObject<HTMLElement>
}

function ApplicationNotificationsProvider({
  children,
  pageBannerContainerRef,
}: ApplicationNotificationsProviderProps) {
  const notifierRef = useRef<{
    debuggerNotifier?: DebuggingNotifier
    toastNotifier?: ToastNotifier
    overlayBannerNotifier?: OverlayBannerNotifier
    pageBannerNotifier?: PageBannerNotifier
  }>({})
  const [debuggerNotifier, setDebuggerNotifier] = useState(
    new DebuggingNotifier()
  )
  const [toastNotifier, setToastNotifier] = useState(new ToastNotifier())
  const [overlayBannerNotifier, setOverlayBannerNotifier] = useState(
    new OverlayBannerNotifier()
  )
  const [pageBannerNotifier, setPageBannerNotifier] = useState(
    new PageBannerNotifier()
  )

  // hold a reference to the notifiers for use in cleanup
  notifierRef.current = {
    debuggerNotifier,
    toastNotifier,
    overlayBannerNotifier,
    pageBannerNotifier,
  }

  useMountEffect(() => {
    debuggerNotifier.initialize()
    toastNotifier.initialize()
    overlayBannerNotifier.initialize()
    pageBannerNotifier.initialize(pageBannerContainerRef)

    // update state so context is aware of initialization
    setDebuggerNotifier(debuggerNotifier)
    setToastNotifier(toastNotifier)
    setOverlayBannerNotifier(overlayBannerNotifier)
    setPageBannerNotifier(pageBannerNotifier)

    return () => {
      const {
        debuggerNotifier,
        toastNotifier,
        overlayBannerNotifier,
        pageBannerNotifier,
      } = notifierRef.current

      debuggerNotifier?.destroy?.()
      toastNotifier?.destroy?.()
      overlayBannerNotifier?.destroy?.()
      pageBannerNotifier?.destroy?.()
    }
  })

  const context = {
    debugger: debuggerNotifier,
    toast: toastNotifier,
    overlayBanner: overlayBannerNotifier,
    pageBanner: pageBannerNotifier,
    roles: NOTIFICATION_ROLES,
  }

  // set the bridge context for use in redux actions (non-component access)
  setApplicationNotificationsBridgeContext(context)

  return (
    <ApplicationNotificationsContext.Provider value={context}>
      {children}
    </ApplicationNotificationsContext.Provider>
  )
}

ApplicationNotificationsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  pageBannerContainerRef: PropTypes.object,
}

/**
 * For use in functional components
 */
const useApplicationNotifications = () => {
  const context = useContext(ApplicationNotificationsContext)
  return context
}

/**
 * For use with class components
 */
interface WithApplicationNotificationsContextProps {
  notifications: Notifications
}

const withApplicationNotificationsContext =
  <P extends object>(
    Component: React.ComponentType<P & WithApplicationNotificationsContextProps>
  ) =>
  (props: P) =>
    (
      <ApplicationNotificationsContext.Consumer>
        {context => <Component {...props} notifications={context} />}
      </ApplicationNotificationsContext.Consumer>
    )

/**
 * For testing purposes
 * */
interface MockApplicationNotificationsProviderProps {
  children: React.ReactNode
  context: Notifications
}
const MockApplicationNotificationsProvider = ({
  children,
  context,
}: MockApplicationNotificationsProviderProps) => (
  <ApplicationNotificationsContext.Provider value={context}>
    {children}
  </ApplicationNotificationsContext.Provider>
)

MockApplicationNotificationsProvider.propTypes = {
  children: PropTypes.node,
  context: PropTypes.object,
}

// see also ../index.js for the export (which uses named exports, not "*")
export {
  ApplicationNotificationsProvider,
  ApplicationNotificationsConsumer,
  useApplicationNotifications, // hook for functional components
  withApplicationNotificationsContext, // HOC for class components
  getApplicationNotificationsBridgeContext, // bridge for redux actions
  NOTIFICATION_ROLES,
  DEFAULT_NOTIFICATION_OPTIONS,
  getNullApplicationNotifications,
  MockApplicationNotificationsProvider,
}
